import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Send, Loader2, ThumbsUp, ThumbsDown, Share2, Copy, Paperclip } from 'lucide-react'
import { getChatStatus, sendChatMessage, type ChatMessage } from '@/services/chatApiService'

interface ChatModalProps {
  open: boolean
  onClose: () => void
}

export default function ChatModal({ open, onClose }: ChatModalProps) {
  const { t, i18n } = useTranslation()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  
  // Check if current language is RTL
  const isRTL = i18n.language?.split('-')[0] === 'ar' || i18n.language?.split('-')[0] === 'ur'

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (open) {
      scrollToBottom()
      inputRef.current?.focus()
    }
  }, [open, messages])

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`
    }
  }, [input])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || loading) return

    setInput('')
    setError(null)
    const userMessage: ChatMessage = { role: 'user', content: text }
    setMessages((prev) => [...prev, userMessage])
    setLoading(true)

    try {
      const history = [...messages, userMessage].map((m) => ({ role: m.role, content: m.content }))
      const { reply } = await sendChatMessage({
        message: text,
        conversationHistory: history.slice(-20),
      })
      setMessages((prev) => [...prev, { role: 'assistant', content: reply || '' }])
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string }; status?: number } })?.response?.data?.error
        || (err as Error)?.message
        || t('chat.error', 'حدث خطأ. حاول مرة أخرى.')
      setError(msg)
      setMessages((prev) => [...prev, { role: 'assistant', content: msg }])
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[800px] max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 z-50"
        role="dialog"
        aria-modal="true"
        aria-label="ChatGPT"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            ChatGPT
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
            aria-label={t('common.close', 'إغلاق')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[60vh] bg-white dark:bg-gray-800">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('chat.placeholder', 'اسأل أي سؤال عن الإسلام أو القرآن.')}
              </p>
            </div>
          )}
          <div className="space-y-6 p-4">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-4 ${m.role === 'user' ? (isRTL ? 'flex-row-reverse' : 'flex-row') : (isRTL ? 'flex-row' : 'flex-row')}`}
              >
                {/* Avatar */}
                <div
                  className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-medium ${
                    m.role === 'user'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-800 dark:bg-gray-700 text-white'
                  }`}
                >
                  {m.role === 'user' ? 'Y' : 'C'}
                </div>

                {/* Message Content */}
                <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {m.role === 'user' ? 'You' : 'ChatGPT'}
                    </span>
                  </div>
                  <div className={`text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words ${isRTL ? 'text-right' : 'text-left'}`}>
                    {m.content}
                  </div>
                  
                  {/* Action Icons for AI messages */}
                  {m.role === 'assistant' && (
                    <div className="flex items-center gap-2 mt-2 opacity-60 hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => {}}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Thumbs up"
                      >
                        <ThumbsUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {}}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Thumbs down"
                      >
                        <ThumbsDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {}}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Share"
                      >
                        <Share2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopy(m.content)}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Copy"
                      >
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-gray-800 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center text-sm font-medium text-white">
                  C
                </div>
                <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 mb-1 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      ChatGPT
                    </span>
                  </div>
                  <div className={`flex items-center gap-2 text-gray-600 dark:text-gray-400 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('chat.typing', 'جاري الكتابة...')}</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {error && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">{error}</p>
          )}
          <div className={`flex items-end gap-2 ${isRTL ? 'flex-row' : 'flex-row-reverse'}`}>
            <button
              type="button"
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition-colors"
              aria-label="Attach file"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Message ChatGPT"
                rows={1}
                dir={isRTL ? 'rtl' : 'ltr'}
                className={`w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 px-4 py-3 ${isRTL ? 'pr-12' : 'pl-12'} text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isRTL ? 'text-right' : 'text-left'}`}
                disabled={loading}
                style={{ maxHeight: '120px' }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className={`absolute bottom-2 ${isRTL ? 'right-2' : 'right-2'} p-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                aria-label={t('chat.send', 'إرسال')}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* Disclaimer */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            ChatGPT can make mistakes. Consider checking important information.
          </p>
        </div>
      </div>
    </>
  )
}
